import React, { useState } from "react";

import { Controller, useFormContext } from "react-hook-form";
import { RequiredItem } from "@transfr-inc/dashboard-components";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
  Input,
  Textarea,
} from "@transfr-inc/dashboard-components/forms";
import "./activity-template.scss";
import {
  AllContentTypes,
  getContentTypeById,
  getContentTypeDisplayNameById,
} from "@transfr-inc/dashboard-components/curriculum";
import { UploadZipInput } from "@views/library/components/upload-zip.input";

const OutOfHeadsetTypes = AllContentTypes.filter((t) => [2, 3].includes(t.id));

export const ActivityForm = ({
  defaultValues,
  register,
  control,
  formState,
}) => {
  if (!register && !formState && !control) {
    ({ register, formState, control } = useFormContext());
  } else if (!register || !formState || !control) {
    throw (
      "ActivityForm: for register, control, and formState, all values must be specified or entirely left unspecified " +
      "Specifying a value for only the errors prop, for example, will result in this error message."
    );
  }

  const { errors } = formState;

  const originalFileName =
    defaultValues?.fileName || defaultValues?.file_name || null;
  const [hasDeletedExistingFile, setHasDeletedExistingFile] = useState(false);

  const renderNameInput = () => {
    return (
      <Controller
        control={control}
        name="title"
        defaultValue={defaultValues?.title ?? ""}
        rules={{
          required: {
            value: true,
            message: "Required fields are missing.",
          },
          maxLength: 255,
          pattern: {
            value: /^[\d ',.a-z-]+$/i,
            message:
              "Name formatted incorrectly. Only letters, hyphens, numbers, and spaces are allowed.",
          },
        }}
        render={({ field: { onBlur, onChange, value, ref } }) => {
          return (
            <div className="activity-template-field">
              <h3>
                Name <sup>*</sup>
              </h3>
              <Input
                label={
                  <RequiredItem
                    className="activity-template-input-label"
                    text="Name"
                  />
                }
                type="text"
                name="title"
                errors={errors?.title}
                ref={ref}
                onBlur={onBlur}
                onChange={onChange}
                value={value ?? ""}
                defaultValue={defaultValues?.title || ""}
              />
            </div>
          );
        }}
      />
    );
  };

  const renderDescriptionTextarea = () => {
    return (
      <Controller
        control={control}
        name="description"
        defaultValue={defaultValues?.description || ""}
        rules={{
          required: {
            value: false,
          },
        }}
        render={({ field: { onBlur, onChange, value, ref } }) => {
          return (
            <div className="activity-template-field">
              <Textarea
                label="Description"
                name="description"
                ref={ref}
                onBlur={onBlur}
                onChange={onChange}
                value={value ?? ""}
                defaultValue={defaultValues?.description}
              />
            </div>
          );
        }}
      />
    );
  };

  const renderActivityTypeArea = () => {
    return (
      <Controller
        control={control}
        name="contentType"
        defaultValue={defaultValues?.activityType || ""}
        rules={{
          required: {
            value: true,
          },
        }}
        render={({ field: { onBlur, onChange, value, ref } }) => {
          const contentType = getContentTypeById(value);
          const contentTypeIcon = contentType?.icon;
          return (
            <div className="activity-template-field">
              <h3>
                Activity Type <sup>*</sup>
              </h3>
              <Dropdown
                selected={contentType?.id}
                onOptionSelected={(option) => onChange(option.id)}
              >
                <DropdownButton
                  placeholder={"Select an activity type..."}
                  icon={contentTypeIcon}
                >
                  {getContentTypeDisplayNameById(contentType?.id)}
                </DropdownButton>
                <DropdownItems>
                  {OutOfHeadsetTypes.map((type) => (
                    <DropdownItem key={type.id} value={type} icon={type.icon}>
                      {type.label}
                    </DropdownItem>
                  ))}
                </DropdownItems>
              </Dropdown>
            </div>
          );
        }}
      />
    );
  };

  const renderUploadZipArea = () => {
    const isFileRequired = !originalFileName || hasDeletedExistingFile;

    return (
      <Controller
        control={control}
        name="uploadZip"
        defaultValue={undefined}
        rules={{
          required: isFileRequired ? "A file is required." : false,
          validate: (value) => {
            if (isFileRequired && !(value instanceof File)) {
              return "Please upload a file.";
            }
            if (value && !(value instanceof File)) {
              return "Invalid file.";
            }
            return true;
          },
        }}
        render={({ field: { value, onChange } }) => {
          const handleChange = (newValue) => {
            if (newValue === undefined || newValue === null) {
              if (originalFileName) {
                setHasDeletedExistingFile(true);
              }
              onChange();
            } else if (newValue instanceof File) {
              setHasDeletedExistingFile(false);
              onChange(newValue);
            } else {
              onChange(newValue);
            }
          };

          return (
            <div className="activity-template-field">
              <h3>Upload New File (.zip) {isFileRequired && <sup>*</sup>}</h3>
              <UploadZipInput
                onChange={handleChange}
                value={value ?? undefined}
                existingFileName={
                  !value && !hasDeletedExistingFile ? originalFileName : null
                }
                errors={
                  errors?.uploadZip
                    ? typeof errors.uploadZip === "string"
                      ? errors.uploadZip
                      : errors.uploadZip?.message || errors.uploadZip
                    : undefined
                }
              />
            </div>
          );
        }}
      />
    );
  };

  return (
    <div className="activity-template-form">
      {renderNameInput()}
      {renderDescriptionTextarea()}
      {renderActivityTypeArea()}
      {renderUploadZipArea()}
    </div>
  );
};
